import axiosInstance from '../axios/axiosInstance';

const PAGE_SIZE = 20;

export const TECH_STATUSES = ['pending', 'in_progress', 'completed', 'returned'];

export function formatTechPrice(value) {
    if (value == null || value === '') return null;
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    return n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function lastReturnReason(history = []) {
    for (let i = history.length - 1; i >= 0; i -= 1) {
        const item = history[i];
        if (item?.type === 'status_changed' && item?.to_status === 'returned') {
            return item.description || null;
        }
    }
    return null;
}

export function firstApiError(error) {
    const data = error?.response?.data;
    const errors = data?.errors;
    if (errors && typeof errors === 'object') {
        const first = Object.values(errors).flat().find(Boolean);
        if (first) return String(first);
    }
    return data?.message || null;
}

function listParams({ patientId, statuses, page = 1, perPage = PAGE_SIZE } = {}) {
    const params = new URLSearchParams();
    if (patientId) params.set('patient_id', String(patientId));
    if (Array.isArray(statuses) && statuses.length) {
        statuses.forEach((status) => params.append('status[]', status));
    }
    params.set('page', String(page));
    params.set('per_page', String(perPage));
    return params;
}

function normalizeList(payload) {
    const nested = payload?.data;
    const body = nested && !Array.isArray(nested) && Array.isArray(nested.data) ? nested : payload;
    const list = Array.isArray(body?.data)
        ? body.data
        : Array.isArray(nested)
            ? nested
            : [];
    const meta = body?.meta ?? payload?.meta ?? {};
    const count = Number(body?.count ?? payload?.count ?? meta.total ?? list.length) || 0;
    return {
        items: list,
        count,
        meta,
        links: body?.links ?? payload?.links ?? null,
        pagination: {
            current: Number(meta.current_page ?? 1) || 1,
            pageSize: Number(meta.per_page ?? PAGE_SIZE) || PAGE_SIZE,
            total: count,
        },
    };
}

function unwrapItem(payload) {
    const data = payload?.data;
    if (data && !Array.isArray(data) && data.id != null) return data;
    return data ?? payload;
}

async function fetchAllPages(url) {
    const all = [];
    let page = 1;
    let last = 1;
    do {
        const response = await axiosInstance.get(url, { params: { page, per_page: 100 } });
        const parsed = normalizeList(response.data);
        all.push(...parsed.items);
        last = Number(parsed.meta?.last_page ?? 1) || 1;
        page += 1;
    } while (page <= last);
    return all;
}

export async function fetchTechnicianObjects(scope, { page = 1, perPage = PAGE_SIZE } = {}) {
    const path = scope === 'doctor' ? '/doctor/technician-objects' : '/admin/technician-objects';
    const response = await axiosInstance.get(path, { params: { page, per_page: perPage } });
    return normalizeList(response.data);
}

export async function fetchAllTechnicianObjects(scope) {
    const path = scope === 'doctor' ? '/doctor/technician-objects' : '/admin/technician-objects';
    return fetchAllPages(path);
}

export async function fetchTechnicianObject(id) {
    const response = await axiosInstance.get(`/admin/technician-objects/${id}`);
    return unwrapItem(response.data);
}

export async function createTechnicianObject(body) {
    const response = await axiosInstance.post('/admin/technician-objects', body);
    return unwrapItem(response.data);
}

export async function updateTechnicianObject(id, body) {
    const response = await axiosInstance.patch(`/admin/technician-objects/${id}`, body);
    return unwrapItem(response.data);
}

export async function deleteTechnicianObject(id) {
    await axiosInstance.delete(`/admin/technician-objects/${id}`);
}

export async function fetchTechnicians() {
    const response = await axiosInstance.get('/doctor/technicians');
    const data = response.data?.data;
    return Array.isArray(data) ? data : [];
}

function requestsPath(scope) {
    if (scope === 'doctor') return '/doctor/technician-requests';
    if (scope === 'technician') return '/technician/requests';
    return '/admin/technician-requests';
}

export async function fetchTechnicianRequests(scope, query) {
    const params = listParams(query);
    const response = await axiosInstance.get(`${requestsPath(scope)}?${params.toString()}`);
    return normalizeList(response.data);
}

export async function fetchTechnicianRequest(scope, id) {
    const response = await axiosInstance.get(`${requestsPath(scope)}/${id}`);
    return unwrapItem(response.data);
}

export async function createTechnicianRequest(body) {
    const response = await axiosInstance.post('/doctor/technician-requests', body);
    return unwrapItem(response.data);
}

export async function returnTechnicianRequest(id, description) {
    const response = await axiosInstance.post(`/doctor/technician-requests/${id}/return`, { description });
    return unwrapItem(response.data);
}

export async function startTechnicianRequest(id, description) {
    const body = description ? { description } : {};
    const response = await axiosInstance.post(`/technician/requests/${id}/start`, body);
    return unwrapItem(response.data);
}

export async function completeTechnicianRequest(id, description) {
    const body = description ? { description } : {};
    const response = await axiosInstance.post(`/technician/requests/${id}/complete`, body);
    return unwrapItem(response.data);
}

export async function updateTechnicianRequestPrice(id, { price, description }) {
    const body = { price };
    if (description) body.description = description;
    const response = await axiosInstance.patch(`/admin/technician-requests/${id}/price`, body);
    return unwrapItem(response.data);
}

export async function createTechnicianAccount({ name, login, phone, password }) {
    const response = await axiosInstance.post('/admin/technicians', { name, login, phone, password });
    return unwrapItem(response.data);
}

function extractArray(payload) {
    const parsed = normalizeList(payload);
    if (parsed.items.length) return { items: parsed.items, lastPage: Number(parsed.meta?.last_page) || 1 };
    const data = payload?.data ?? payload;
    if (Array.isArray(data)) return { items: data, lastPage: 1 };
    if (data && typeof data === 'object') {
        for (const key of ['teeth', 'teethes', 'items', 'list']) {
            if (Array.isArray(data[key])) return { items: data[key], lastPage: 1 };
        }
    }
    return { items: [], lastPage: 1 };
}

export function toothFdi(item) {
    if (item == null || typeof item !== 'object') return null;
    const raw = item.number ?? item.fdi ?? item.code ?? item.tooth_number;
    if (raw == null || raw === '') return null;
    if (typeof raw === 'object') return null;
    const n = Number(String(raw).replace(/[^\d]/g, ''));
    return Number.isFinite(n) && n > 0 ? n : null;
}

export function findToothByFdi(list, fdi, type) {
    const n = Number(fdi);
    const matches = (list || []).filter((item) => toothFdi(item) === n);
    if (!matches.length) {
        return (list || []).find((item) => Number(item.id) === n) ?? null;
    }
    if (type) {
        const typed = matches.find((item) => (item.type || 'adult') === type);
        if (typed) return typed;
    }
    return matches[0];
}

export async function fetchTeethes() {
    const response = await axiosInstance.get('/teethes', { params: { page: 1, per_page: 100 } });
    const extracted = extractArray(response.data);
    if (extracted.lastPage > 1) {
        return fetchAllPages('/teethes');
    }
    return extracted.items;
}

export function toothNumberById(teethList, teethId) {
    if (teethId == null || teethId === '') return null;
    const tooth = (teethList || []).find((item) => Number(item.id) === Number(teethId));
    return toothFdi(tooth) ?? tooth?.number ?? null;
}

export function apiFieldErrors(error) {
    const errors = error?.response?.data?.errors;
    if (!errors || typeof errors !== 'object') return {};
    const mapped = {};
    Object.entries(errors).forEach(([key, value]) => {
        mapped[key] = Array.isArray(value) ? value[0] : value;
    });
    return mapped;
}

export { PAGE_SIZE };
